import React, { useState, useEffect } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Check, X, Eye, MessageSquare, Calendar, User, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Suggestion {
  id: string;
  user_id: string;
  title: string;
  description: string;
  status: 'pending' | 'approved' | 'rejected' | 'implemented' | 'em_desenvolvimento';
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
}

interface User {
  id: string;
  email: string;
  display_name: string | null;
}

const SuggestionsTable = () => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [adminNotes, setAdminNotes] = useState("");
  const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null);
  const { toast } = useToast();

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Carregar sugestões
      const { data: suggestionsData, error: suggestionsError } = await supabase
        .from('suggestions')
        .select('*')
        .order('created_at', { ascending: false });

      if (suggestionsError) throw suggestionsError;

      // Carregar usuários
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, email, display_name');

      if (usersError) throw usersError;

      setSuggestions((suggestionsData || []) as Suggestion[]);
      setUsers(usersData || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as sugestões.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const getUserInfo = (userId: string) => {
    return users.find(u => u.id === userId);
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      pending: { variant: "secondary" as const, label: "Pendente" },
      approved: { variant: "default" as const, label: "Aprovada" },
      rejected: { variant: "destructive" as const, label: "Rejeitada" },
      implemented: { variant: "default" as const, label: "Implementada" },
      em_desenvolvimento: { variant: "outline" as const, label: "Em Desenvolvimento" }
    };
    
    const config = variants[status as keyof typeof variants] || variants.pending;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const handleDeleteSuggestion = async (suggestionId: string) => {
    try {
      const { error } = await supabase
        .from('suggestions')
        .delete()
        .eq('id', suggestionId);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Sugestão excluída com sucesso.",
      });

      await loadData();
    } catch (error) {
      console.error('Erro ao excluir sugestão:', error);
      toast({
        title: "Erro",
        description: "Não foi possível excluir a sugestão.",
        variant: "destructive",
      });
    }
  };

  const handleUpdateStatus = async (suggestionId: string, newStatus: string, notes?: string) => {
    try {
      const { error } = await supabase
        .from('suggestions')
        .update({ 
          status: newStatus,
          admin_notes: notes || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', suggestionId);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: `Sugestão ${newStatus === 'approved' ? 'aprovada' : newStatus === 'rejected' ? 'rejeitada' : 'atualizada'} com sucesso.`,
      });

      await loadData();
    } catch (error) {
      console.error('Erro ao atualizar sugestão:', error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar a sugestão.",
        variant: "destructive",
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-6 w-6 animate-spin" />
        <span className="ml-2">Carregando sugestões...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Sugestões dos Usuários</h3>
        <Button onClick={loadData} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Usuário</TableHead>
              <TableHead>Título</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Data</TableHead>
              <TableHead>Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {suggestions.map((suggestion) => {
              const userInfo = getUserInfo(suggestion.user_id);
              
              return (
                <TableRow key={suggestion.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{userInfo?.display_name || 'Usuário'}</p>
                        <p className="text-sm text-muted-foreground">{userInfo?.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  
                  <TableCell>
                    <div>
                      <p className="font-medium">{suggestion.title}</p>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {suggestion.description}
                      </p>
                    </div>
                  </TableCell>
                  
                  <TableCell>
                    {getStatusBadge(suggestion.status)}
                  </TableCell>
                  
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        {formatDate(suggestion.created_at)}
                      </span>
                    </div>
                  </TableCell>
                  
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedSuggestion(suggestion)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>{suggestion.title}</DialogTitle>
                            <DialogDescription>
                              Enviada por {userInfo?.display_name || userInfo?.email} em {formatDate(suggestion.created_at)}
                            </DialogDescription>
                          </DialogHeader>
                          
                          <div className="space-y-4">
                            <div>
                              <h4 className="font-medium mb-2">Descrição:</h4>
                              <p className="text-sm text-muted-foreground bg-muted p-3 rounded">
                                {suggestion.description}
                              </p>
                            </div>
                            
                            <div>
                              <h4 className="font-medium mb-2">Status atual:</h4>
                              {getStatusBadge(suggestion.status)}
                            </div>
                            
                            {suggestion.admin_notes && (
                              <div>
                                <h4 className="font-medium mb-2">Notas do administrador:</h4>
                                <p className="text-sm text-muted-foreground bg-muted p-3 rounded">
                                  {suggestion.admin_notes}
                                </p>
                              </div>
                            )}
                            
                             {suggestion.status === 'pending' && (
                               <div className="space-y-3">
                                 <h4 className="font-medium">Ações:</h4>
                                 <Textarea
                                   placeholder="Adicionar notas (opcional)"
                                   value={adminNotes}
                                   onChange={(e) => setAdminNotes(e.target.value)}
                                 />
                                 <div className="flex gap-2">
                                   <Button
                                     onClick={() => {
                                       handleUpdateStatus(suggestion.id, 'em_desenvolvimento', adminNotes);
                                       setAdminNotes("");
                                     }}
                                     className="bg-purple-600 hover:bg-purple-700"
                                   >
                                     <Check className="h-4 w-4 mr-2" />
                                     Em Desenvolvimento
                                   </Button>
                                   <Button
                                     onClick={() => {
                                       handleUpdateStatus(suggestion.id, 'approved', adminNotes);
                                       setAdminNotes("");
                                     }}
                                     className="bg-green-600 hover:bg-green-700"
                                   >
                                     <Check className="h-4 w-4 mr-2" />
                                     Aprovar
                                   </Button>
                                   <Button
                                     onClick={() => {
                                       handleUpdateStatus(suggestion.id, 'rejected', adminNotes);
                                       setAdminNotes("");
                                     }}
                                     variant="destructive"
                                   >
                                     <X className="h-4 w-4 mr-2" />
                                     Rejeitar
                                   </Button>
                                 </div>
                               </div>
                             )}
                             
                             {suggestion.status === 'approved' && (
                               <div className="space-y-3">
                                 <Button
                                   onClick={() => handleUpdateStatus(suggestion.id, 'implemented')}
                                   className="bg-blue-600 hover:bg-blue-700"
                                 >
                                   <Check className="h-4 w-4 mr-2" />
                                   Marcar como Implementada
                                 </Button>
                               </div>
                             )}

                             <div className="mt-4 pt-4 border-t">
                               <Button
                                 onClick={() => handleDeleteSuggestion(suggestion.id)}
                                 variant="destructive"
                                 size="sm"
                               >
                                 <X className="h-4 w-4 mr-2" />
                                 Excluir Sugestão
                               </Button>
                             </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                      
                       {suggestion.status === 'pending' && (
                         <>
                           <Button
                             onClick={() => handleUpdateStatus(suggestion.id, 'em_desenvolvimento')}
                             size="sm"
                             className="bg-purple-600 hover:bg-purple-700"
                           >
                             <Check className="h-4 w-4" />
                           </Button>
                           <Button
                             onClick={() => handleUpdateStatus(suggestion.id, 'approved')}
                             size="sm"
                             className="bg-green-600 hover:bg-green-700"
                           >
                             <Check className="h-4 w-4" />
                           </Button>
                           <Button
                             onClick={() => handleUpdateStatus(suggestion.id, 'rejected')}
                             size="sm"
                             variant="destructive"
                           >
                             <X className="h-4 w-4" />
                           </Button>
                         </>
                       )}
                       
                       {suggestion.status === 'approved' && (
                         <Button
                           onClick={() => handleUpdateStatus(suggestion.id, 'implemented')}
                           size="sm"
                           className="bg-blue-600 hover:bg-blue-700"
                         >
                           <MessageSquare className="h-4 w-4" />
                         </Button>
                       )}

                       <Button
                         onClick={() => handleDeleteSuggestion(suggestion.id)}
                         size="sm"
                         variant="destructive"
                       >
                         <X className="h-4 w-4" />
                       </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            
            {suggestions.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  Nenhuma sugestão encontrada
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default SuggestionsTable;